import { isApiError } from '@botpress/client'
import { Suspense, useState } from 'react'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { logout } from '../services'
import { config, isHttpUnauthorizedError } from '../shared'
import { PartnerStackProvider } from './PartnerStack'
import { PatProvider } from './PatProvider'
import { ReactQueryProvider } from './ReactQuery'
import { UnleashProvider } from './Unleash'
import { UserProvider } from './UserProvider'
import { WorkspaceProvider } from './WorkspaceProvider'
import { SegmentProvider } from './SegmentProvider'
import { captureException } from '@sentry/react'
import { DarkModeProvider } from '~/providers/DarkModeProvider'
import { BpSpinner } from '~/elements'
import { ErrorState } from '~/components'
import { ThemeProvider } from '@bpinternal/ui-kit'
import { PortalProvider } from './PortalProvier'
import { IntercomProvider } from '~/providers/IntercomProvider'
import { NovuProvider } from './NovuProvider'
import { ReplayProvider } from './ReplayProvider'

type Props = {
  children: React.ReactNode
}

export const Providers = ({ children }: Props) => {
  const [errorId, setErrorId] = useState<string>()

  function handleError({ error }: FallbackProps) {
    if (isHttpUnauthorizedError(error) && !config.usePat) {
      // eslint-disable-next-line no-console
      console.debug('Unauthorized error received from API, logging out...')
      logout()
      return null
    }

    if (isApiError(error)) {
      setErrorId(error.id)
    }

    console.error(error)
    captureException(error)

    return (
      <ErrorState
        title="Sorry, something went wrong"
        description={`Try to refresh this page or please contact us if the problem persists.${errorId ? ` (Error ID: ${errorId})` : ''}`}
        navigateToHome={() => {
          window.location.href = '/'
        }}
        showStatusPageLink={true}
      />
    )
  }

  return (
    <>
      <DarkModeProvider>
        <ThemeProvider>
          <ErrorBoundary fallbackRender={handleError}>
            <ReactQueryProvider>
              <Suspense
                fallback={
                  <div className="flex h-screen items-center justify-center">
                    <div className="size-20">
                      <BpSpinner />
                    </div>
                  </div>
                }
              >
                <PatProvider>
                  <UserProvider>
                    <SegmentProvider>
                      <UnleashProvider>
                        <ReplayProvider />
                        <WorkspaceProvider>
                          <PortalProvider>
                            <IntercomProvider>
                              <NovuProvider>{children}</NovuProvider>
                            </IntercomProvider>
                          </PortalProvider>
                        </WorkspaceProvider>
                        <PartnerStackProvider />
                      </UnleashProvider>
                    </SegmentProvider>
                  </UserProvider>
                </PatProvider>
              </Suspense>
            </ReactQueryProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </DarkModeProvider>
    </>
  )
}
